(() => {
  const configNode = document.getElementById('CozyTreeBundleCartConfig');
  if (!configNode) return;

  let config;
  try {
    config = JSON.parse(configNode.textContent || '{}');
  } catch (error) {
    console.error('CozyTree bundle config:', error);
    return;
  }

  const giftVariantId = String(config.giftVariantId || '');
  if (!giftVariantId) return;

  const BUILD_PROPERTY = '_cozytree_build_id';
  const MANAGED_GIFT_PROPERTY = '_cozytree_mystery_gift';
  let pending = null;
  let giftBoxPending = false;

  function onPreviewError(event) {
    const image = event.target;
    if (!(image instanceof HTMLImageElement) || !image.matches('[data-cozy-preview]') || image.dataset.cozyPreviewFallbackApplied === 'true') return;
    const fallback = image.dataset.cozyPreviewFallback;
    if (!fallback) return;
    image.dataset.cozyPreviewFallbackApplied = 'true';
    image.removeAttribute('data-cozy-preview');
    image.src = fallback;
  }

  document.addEventListener('error', onPreviewError, true);

  function previewModalFrom(node) {
    return node?.closest?.('cart-drawer')?.querySelector?.('[data-cozy-cart-preview-modal]') || null;
  }

  function closeCartPreview(modal) {
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    const image = modal.querySelector('[data-cozy-cart-preview-image]');
    if (image) image.removeAttribute('src');
    const trigger = modal._cozyPreviewTrigger;
    modal._cozyPreviewTrigger = null;
    if (trigger?.isConnected) trigger.focus();
  }

  function openCartPreview(trigger) {
    const modal = previewModalFrom(trigger);
    const source = trigger.querySelector('img');
    const image = modal?.querySelector('[data-cozy-cart-preview-image]');
    if (!modal || !source || !image) return;
    image.src = source.currentSrc || source.src;
    image.alt = source.alt || '';
    modal._cozyPreviewTrigger = trigger;
    modal.hidden = false;
    modal.querySelector('[data-cozy-cart-preview-close]')?.focus();
  }

  function onPreviewClick(event) {
    const trigger = event.target.closest?.('[data-cozy-cart-preview-trigger]');
    if (trigger) {
      event.preventDefault();
      openCartPreview(trigger);
      return;
    }
    const close = event.target.closest?.('[data-cozy-cart-preview-close]');
    const modal = event.target.closest?.('[data-cozy-cart-preview-modal]');
    if (close || (modal && event.target === modal)) closeCartPreview(modal || previewModalFrom(close));
  }

  function onPreviewKeydown(event) {
    if (event.key !== 'Escape') return;
    const modal = document.querySelector('[data-cozy-cart-preview-modal]:not([hidden])');
    if (!modal) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    closeCartPreview(modal);
  }

  document.addEventListener('click', onPreviewClick);
  document.addEventListener('keydown', onPreviewKeydown, true);

  const request = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(async (response) => {
      const data = await response.json();
      if (!response.ok || data?.errors) throw new Error(data?.description || data?.message || 'Cart update failed');
      return data;
    });

  const fetchCart = () => fetch(`${routes.cart_url}.js`, { credentials: 'same-origin' }).then((response) => response.json());

  function personalizedQuantity(cart) {
    return (cart?.items || []).reduce(
      (total, item) => (item?.properties?.[BUILD_PROPERTY] ? total + (Number(item.quantity) || 0) : total),
      0,
    );
  }

  function desiredGiftQuantity(cart) {
    return personalizedQuantity(cart) >= 3 ? 3 : 0;
  }

  function managedGiftLines(cart) {
    return (cart?.items || []).filter((item) => item?.properties?.[MANAGED_GIFT_PROPERTY] === 'true');
  }

  async function changeManagedLine(key, quantity) {
    return request(routes.cart_change_url, { id: key, quantity });
  }

  async function addManagedGifts(quantity) {
    return request(routes.cart_add_url, {
      items: [
        {
          id: Number(giftVariantId),
          quantity,
          properties: { [MANAGED_GIFT_PROPERTY]: 'true' },
        },
      ],
    });
  }

  function publishCartUpdate(cart) {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cozytree-bundle-cart', cartData: cart });
  }

  async function onGiftBoxClick(event) {
    const button = event.target.closest?.('[data-cozy-giftbox-add]');
    if (!button || giftBoxPending) return;
    const variantId = Number(button.dataset.variantId || 0);
    if (!variantId) return;

    event.preventDefault();
    giftBoxPending = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      await request(routes.cart_add_url, { items: [{ id: variantId, quantity: 1 }] });
      const cart = await fetchCart();
      publishCartUpdate(cart);
    } catch (error) {
      console.error('CozyTree gift box add:', error);
      button.disabled = false;
      button.removeAttribute('aria-busy');
    } finally {
      giftBoxPending = false;
    }
  }

  document.addEventListener('click', onGiftBoxClick);

  async function reconcileNow() {
    let cart = await fetchCart();
    const desired = desiredGiftQuantity(cart);
    const managed = managedGiftLines(cart);
    let changed = false;

    if (desired === 0) {
      for (const line of managed) {
        if (Number(line.quantity) > 0) {
          await changeManagedLine(line.key, 0);
          changed = true;
        }
      }
    } else if (managed.length === 0) {
      await addManagedGifts(desired);
      changed = true;
    } else {
      const [primary, ...duplicates] = managed;
      if (Number(primary.quantity) !== desired) {
        await changeManagedLine(primary.key, desired);
        changed = true;
      }
      for (const line of duplicates) {
        if (Number(line.quantity) > 0) {
          await changeManagedLine(line.key, 0);
          changed = true;
        }
      }
    }

    if (changed) {
      cart = await fetchCart();
      publishCartUpdate(cart);
    }
    return cart;
  }

  function reconcile() {
    if (pending) return pending;
    pending = reconcileNow().finally(() => {
      pending = null;
    });
    return pending;
  }

  function openDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      drawer.open(document.querySelector('#cart-icon-bubble'));
      return;
    }
    window.location.assign(routes.cart_url);
  }

  window.CozyTreeBundleCart = {
    reconcile,
    openDrawer,
    personalizedQuantity,
    desiredGiftQuantity,
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source === 'cozytree-bundle-cart') return;
      reconcile().catch((error) => console.error('CozyTree gift reconciliation:', error));
    });
  }
})();
